import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Plus, Search, X, Loader2, LayoutGrid, List, ClipboardList, Trash2,
  User, Phone, MapPin, Calendar, Mail, FileText,
  CheckCircle2, Clock, AlertTriangle, XCircle, Zap, Sparkles,
  ArrowRight, StickyNote, UserCheck, ChevronDown, Star, Inbox,
  Share2, MessageSquare, Copy, CheckCheck, Pencil, Building2, Activity, Gauge,
  Package, Wifi
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { engineerService, type FieldEngineer } from '@/services/firebase/engineers'
import { useAuthStore } from '@/store/authStore'
import { ROUTES } from '@/constants'
import type { Task, TaskStatus, TaskType } from '@/types'
import toast from 'react-hot-toast'

// ── Helper: Determine enquiry workflow phase ───────────────────────────────────
const getEnquiryPhase = (task: Task): { label: string; icon: string; grad: string; dot: string; phase: 'enquiry' | 'feasibility' | 'provisioning' | 'installation' | 'completed' } => {
  const isProvisioned = task.provisioningStage === 'provisioned'
  const isProvisioning = !!task.provisioningStage && task.provisioningStage !== 'pending'
  const isInstallation = !!task.installationPhase && task.installationPhase !== 'pending'
  const isComplete = task.installationPhase === 'completed' || task.status === 'closed'

  if (isComplete) {
    return { label: 'Completed', icon: '✅', grad: 'linear-gradient(135deg,#059669,#10b981)', dot: '#059669', phase: 'completed' }
  }
  if (isInstallation) {
    return { label: 'Installation', icon: '🔧', grad: 'linear-gradient(135deg,#15803d,#22c55e)', dot: '#15803d', phase: 'installation' }
  }
  if (isProvisioned || isProvisioning || task.feasibilityApproved) {
    return { label: 'Provisioning', icon: '📦', grad: 'linear-gradient(135deg,#c2410c,#f97316)', dot: '#c2410c', phase: 'provisioning' }
  }
  if (task.feasibilityStatus) {
    return { label: 'Feasibility', icon: '🔍', grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)', dot: '#6366f1', phase: 'feasibility' }
  }
  return { label: 'Enquiry', icon: '💬', grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', dot: '#8b5cf6', phase: 'enquiry' }
}

// ── Config maps ───────────────────────────────────────────────────────────────
const TYPE_MAP: Record<TaskType, { label: string; icon: string; grad: string; dot: string }> = {
  new_connection: { label: 'New Connection', icon: '🔌', grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', dot: '#2563eb' },
  support:        { label: 'Support',        icon: '🛠️', grad: 'linear-gradient(135deg,#f97316,#ef4444)', dot: '#f97316' },
  enquiry:        { label: 'Enquiry',        icon: '💬', grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', dot: '#8b5cf6' },
  others:         { label: 'Others',         icon: '📋', grad: 'linear-gradient(135deg,#64748b,#475569)', dot: '#64748b' },
}

const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Open',             bg: 'bg-sky-50',     text: 'text-sky-700',    border: 'border-sky-200',    icon: <Inbox size={10} />,        dot: '#0ea5e9' },
  assigned:         { label: 'Assigned',         bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-200',   icon: <User size={10} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700', border: 'border-violet-200', icon: <Zap size={10} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Site Visit',       bg: 'bg-indigo-50',  text: 'text-indigo-700', border: 'border-indigo-200', icon: <MapPin size={10} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Work In Progress', bg: 'bg-orange-50',  text: 'text-orange-700', border: 'border-orange-200', icon: <Zap size={10} />,          dot: '#f97316' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50',   text: 'text-amber-700',  border: 'border-amber-200',  icon: <Clock size={10} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Pending Feedback', bg: 'bg-purple-50',  text: 'text-purple-700', border: 'border-purple-200', icon: <Star size={10} />,         dot: '#a855f7' },
  closed:           { label: 'Closed',           bg: 'bg-emerald-50', text: 'text-emerald-700',border: 'border-emerald-200',icon: <CheckCircle2 size={10} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',   border: 'border-gray-200',   icon: <XCircle size={10} />,      dot: '#9ca3af' },
}

// Engineer-friendly status labels (same styling, more actionable wording)
const ENG_STATUS_LABELS: Record<TaskStatus, string> = {
  open:             'Available',
  assigned:         'New Assignment',
  in_progress:      'In Progress',
  site_visit:       'Go to Site',
  work_in_progress: 'Working',
  pending_approval: 'Waiting for Approval',
  pending_feedback: 'Feedback Required',
  closed:           'Completed',
  cancelled:        'Cancelled',
}

const getStatusLabel = (status: TaskStatus, isEngineer: boolean) =>
  isEngineer ? ENG_STATUS_LABELS[status] : STATUS_CFG[status].label

const STATUS_THEME: Record<TaskStatus, { color: string; shadow: string }> = {
  open:             { color: '#0ea5e9', shadow: 'rgba(14,165,233,0.08)' },
  assigned:         { color: '#3b82f6', shadow: 'rgba(59,130,246,0.08)' },
  in_progress:      { color: '#8b5cf6', shadow: 'rgba(139,92,246,0.08)' },
  site_visit:       { color: '#6366f1', shadow: 'rgba(99,102,241,0.08)' },
  work_in_progress: { color: '#f97316', shadow: 'rgba(249,115,22,0.08)' },
  pending_approval: { color: '#f59e0b', shadow: 'rgba(245,158,11,0.15)' },
  pending_feedback: { color: '#a855f7', shadow: 'rgba(168,85,247,0.08)' },
  closed:           { color: '#10b981', shadow: 'rgba(16,185,129,0.06)' },
  cancelled:        { color: '#9ca3af', shadow: 'rgba(156,163,175,0.04)' },
}

const PRIORITY_CFG: Record<string, { label: string; bar: string; badge: string; badgeText: string }> = {
  low:    { label: 'Low',    bar: '#94a3b8', badge: 'bg-gray-100',    badgeText: 'text-gray-500'   },
  medium: { label: 'Medium', bar: '#3b82f6', badge: 'bg-blue-100',    badgeText: 'text-blue-700'   },
  high:   { label: 'High',   bar: '#f97316', badge: 'bg-orange-100',  badgeText: 'text-orange-700' },
  urgent: { label: 'Urgent', bar: '#ef4444', badge: 'bg-red-100',     badgeText: 'text-red-700'    },
}

const STATUS_FILTERS: { label: string; value: TaskStatus | 'all'; icon: React.ReactNode }[] = [
  { label: 'All',      value: 'all',             icon: <LayoutGrid size={12} /> },
  { label: 'Open',     value: 'open',            icon: <Inbox size={12} /> },
  { label: 'Active',   value: 'in_progress',     icon: <Zap size={12} /> },
  { label: 'Approval', value: 'pending_approval',icon: <Clock size={12} /> },
  { label: 'Closed',   value: 'closed',          icon: <CheckCircle2 size={12} /> },
]

type PhaseFilter = 'all' | 'enquiry' | 'feasibility' | 'provisioning' | 'installation' | 'completed'

const PHASE_FILTERS: { label: string; value: PhaseFilter; icon: React.ReactNode }[] = [
  { label: 'All',          value: 'all',          icon: <LayoutGrid size={12} /> },
  { label: 'Enquiry',      value: 'enquiry',      icon: <ClipboardList size={12} /> },
  { label: 'Feasibility',  value: 'feasibility',  icon: <Zap size={12} /> },
  { label: 'Provisioning', value: 'provisioning', icon: <Package size={12} /> },
  { label: 'Installation', value: 'installation', icon: <Wifi size={12} /> },
  { label: 'Completed',    value: 'completed',    icon: <CheckCircle2 size={12} /> },
]

// Status transitions removed — listing is read-only

// ── Tile Card (grid view) ─────────────────────────────────────────────────────
const TileCard = ({ task, onClick, isEngineer }: {
  task: Task; onClick: () => void; isEngineer: boolean
}) => {
  // For enquiry tasks, use dynamic phase-based theming
  const isEnquiry = task.type === 'enquiry'
  const enquiryPhase = isEnquiry ? getEnquiryPhase(task) : null
  const t = enquiryPhase ?? TYPE_MAP[task.type]
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority]
  const theme = STATUS_THEME[task.status]
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label
  const statusLabel = getStatusLabel(task.status, isEngineer)
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  // Phase-based header gradient for enquiry tasks
  const headerGrad = enquiryPhase
    ? enquiryPhase.grad
    : 'linear-gradient(135deg,#050d1f 0%,#0d1f3c 100%)'

  return (
    <div
      onClick={onClick}
      className="group relative bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl flex flex-col active:scale-[0.98] border-l-[4px]"
      style={{
        boxShadow: `0 2px 12px ${theme.shadow}, 0 0 0 1px rgba(0,0,0,0.04)`,
        borderLeftColor: theme.color
      }}
    >
      {/* Dynamic header with phase-based gradient for enquiry */}
      <div className="relative px-4 pt-4 pb-3 overflow-hidden"
        style={{ background: headerGrad }}>
        {/* Glow orb behind avatar */}
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle,${t.dot}55 0%,transparent 70%)`, filter: 'blur(16px)' }} />
        {/* Priority bar at very top */}
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: p.bar }} />
        <div className="relative z-10 flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl shrink-0 flex items-center justify-center text-white text-sm font-black"
              style={{ background: t.grad, boxShadow: `0 4px 12px ${t.dot}55` }}>
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-black text-white text-[15px] truncate leading-tight">{task.customerName}</p>
              <p className="text-[10px] font-mono text-blue-300/70 mt-0.5 tracking-wider">{task.taskNumber}</p>
            </div>
          </div>
          <span className={`shrink-0 flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
            {s.icon} {statusLabel}
          </span>
        </div>
        {/* Type + priority row */}
        <div className="relative z-10 flex items-center gap-2 mt-3">
          <span className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)' }}>
            {t.icon} {displayType}
          </span>
          <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${p.badge} ${p.badgeText}`}>
            {p.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2 flex-1">
        <div className="flex items-center gap-2 text-[12px] text-gray-600">
          <Phone size={11} className="text-gray-400 shrink-0" />
          <span className="font-semibold">+91 {task.customerPhone}</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-gray-500 truncate">
          <MapPin size={11} className="text-gray-400 shrink-0" />
          <span className="truncate">{task.city}{task.location ? ` · ${task.location}` : ''}</span>
        </div>
        {task.assignedEngineerName && (
          <div className="flex items-center gap-2 text-[11px] text-blue-600">
            <UserCheck size={11} className="shrink-0" />
            <span className="truncate font-semibold">{task.assignedEngineerName}</span>
          </div>
        )}
        {task.description && (
          <p className="text-[11px] text-gray-400 line-clamp-2 leading-relaxed">{task.description}</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 pt-2 border-t border-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          <Calendar size={9} />
          {task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </div>
      </div>
    </div>
  )
}

// ── List Row (list view) ──────────────────────────────────────────────────────
const ListRow = ({ task, onClick, isEngineer }: {
  task: Task; onClick: () => void; isEngineer: boolean
}) => {
  // For enquiry tasks, use dynamic phase-based theming
  const isEnquiry = task.type === 'enquiry'
  const enquiryPhase = isEnquiry ? getEnquiryPhase(task) : null
  const t = enquiryPhase ?? TYPE_MAP[task.type]
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority]
  const theme = STATUS_THEME[task.status]
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label
  const statusLabel = getStatusLabel(task.status, isEngineer)
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 bg-white rounded-2xl px-4 py-3.5 cursor-pointer transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 border-l-[4px]"
      style={{
        boxShadow: `0 1px 4px ${theme.shadow}, 0 0 0 1px rgba(0,0,0,0.04)`,
        borderLeftColor: theme.color
      }}
    >
      {/* Priority stripe */}
      <div className="w-1 self-stretch rounded-full shrink-0" style={{ background: p.bar }} />

      {/* Avatar with phase-based gradient for enquiry */}
      <div className="w-10 h-10 rounded-2xl shrink-0 flex items-center justify-center text-white text-xs font-black"
        style={{ background: t.grad }}>
        {initials}
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-x-4 gap-y-0.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-black text-gray-900 text-sm">{task.customerName}</p>
            <span className="text-[10px] font-mono text-blue-400">{task.taskNumber}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="flex items-center gap-1 text-[11px] text-gray-500">
              <Phone size={9} className="text-gray-400" /> +91 {task.customerPhone}
            </span>
            <span className="flex items-center gap-1 text-[11px] text-gray-500 truncate max-w-[160px]">
              <MapPin size={9} className="text-gray-400 shrink-0" />{task.city}{task.location ? ` · ${task.location}` : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <span className="text-[10px] font-bold shrink-0">{t.icon} {displayType}</span>
          <span className={`flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border shrink-0 ${s.bg} ${s.text} ${s.border}`}>
            {s.icon}{statusLabel}
          </span>
          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full shrink-0 ${p.badge} ${p.badgeText}`}>
            {p.label}
          </span>
        </div>
      </div>

      {/* Date + action */}
      <div className="hidden sm:flex flex-col items-end gap-2 shrink-0">
        <span className="text-[10px] text-gray-400 flex items-center gap-1">
          <Calendar size={9} />
          {task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </span>
      </div>
    </div>
  )
}

// ── Edit Task Modal ───────────────────────────────────────────────────────────
interface EditTaskModalProps {
  task: Task
  onClose: () => void
  onSaved: () => void
}

const EditTaskModal = ({ task, onClose, onSaved }: EditTaskModalProps) => {
  const [customerName, setCustomerName] = useState(task.customerName)
  const [customerPhone, setCustomerPhone] = useState(task.customerPhone)
  const [customerEmail, setCustomerEmail] = useState(task.customerEmail || '')
  const [address, setAddress] = useState(task.address)
  const [location, setLocation] = useState(task.location || '')
  const [city, setCity] = useState(task.city)
  const [serviceNumber, setServiceNumber] = useState(task.serviceNumber || '')
  const [bbUserId, setBbUserId] = useState(task.bbUserId || '')
  const [oltIp, setOltIp] = useState(task.oltIp || '')
  const [vlan, setVlan] = useState(task.vlan ? String(task.vlan) : '')
  const [port, setPort] = useState(task.port ? String(task.port) : '')
  const [mismatchComments, setMismatchComments] = useState(task.mismatchComments || '')
  const [mismatchFlag, setMismatchFlag] = useState(!!task.mismatchFlag)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const e: Record<string, string> = {}
    if (!customerName.trim()) e.customerName = 'Customer Name is required'
    if (!customerPhone.trim()) e.customerPhone = 'Mobile number is required'
    if (!address.trim()) e.address = 'Address is required'
    if (!city.trim()) e.city = 'City is required'
    return e
  }

  const handleSave = async () => {
    const e = validate()
    if (Object.keys(e).length > 0) {
      setErrors(e)
      toast.error('Please fix the highlighted fields')
      return
    }
    setSaving(true)

    const updates = {
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      customerEmail: customerEmail.trim() || undefined,
      address: address.trim(),
      location: location.trim() || undefined,
      city: city.trim(),
      serviceNumber: serviceNumber.trim() || undefined,
      bbUserId: bbUserId.trim() || undefined,
      oltIp: oltIp.trim() || undefined,
      vlan: vlan.trim() ? Number(vlan) : null,
      port: port.trim() ? Number(port) : null,
      mismatchFlag: mismatchFlag,
      mismatchComments: mismatchFlag ? mismatchComments.trim() : '',
    }

    const res = await taskService.update(task.id, updates)
    setSaving(false)
    if (res.success) {
      toast.success('Task details updated successfully!')
      onSaved()
    } else {
      toast.error(res.error ?? 'Failed to update task')
    }
  }

  const iw = "flex items-center gap-2 border border-gray-200 rounded-2xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 bg-white transition-all shadow-sm"
  const ic = "flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent"

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/65 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-modal-enter">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0d1f3c 100%)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center bg-white/10">
              <Pencil size={15} className="text-white" />
            </div>
            <div>
              <h2 className="font-extrabold text-white text-sm">Edit Task Details</h2>
              <p className="text-[10px] text-blue-300/70">Modify customer or technical properties</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="p-6 overflow-y-auto space-y-4 bg-slate-50 flex-1 text-left">
          {/* Personal Info */}
          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Customer Name *</label>
            <div className={iw}>
              <User size={14} className="text-gray-400 shrink-0" />
              <input value={customerName} onChange={e => { setCustomerName(e.target.value); setErrors(er => ({ ...er, customerName: '' })) }} className={ic} />
            </div>
            {errors.customerName && <p className="text-xs text-red-500 mt-1 font-medium">{errors.customerName}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Mobile Number *</label>
              <div className={iw}>
                <Phone size={14} className="text-gray-400 shrink-0" />
                <input value={customerPhone} onChange={e => { setCustomerPhone(e.target.value); setErrors(er => ({ ...er, customerPhone: '' })) }} className={ic} />
              </div>
              {errors.customerPhone && <p className="text-xs text-red-500 mt-1 font-medium">{errors.customerPhone}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Email Address</label>
              <div className={iw}>
                <Mail size={14} className="text-gray-400 shrink-0" />
                <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className={ic} />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Installation Address *</label>
            <div className="border border-gray-200 rounded-2xl px-3.5 py-2.5 bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 transition-all shadow-sm">
              <textarea value={address} onChange={e => { setAddress(e.target.value); setErrors(er => ({ ...er, address: '' })) }} rows={2} className="w-full text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent resize-none font-medium" />
            </div>
            {errors.address && <p className="text-xs text-red-500 mt-1 font-medium">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Building / Landmark</label>
              <div className={iw}>
                <Building2 size={14} className="text-gray-400 shrink-0" />
                <input value={location} onChange={e => setLocation(e.target.value)} className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">City *</label>
              <div className={iw}>
                <input value={city} onChange={e => { setCity(e.target.value); setErrors(er => ({ ...er, city: '' })) }} className={ic} />
              </div>
              {errors.city && <p className="text-xs text-red-500 mt-1 font-medium">{errors.city}</p>}
            </div>
          </div>

          {/* Technical Info */}
          <div className="border-t border-gray-200 pt-3">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">Technical IDs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Service Number</label>
                <div className={iw}>
                  <input value={serviceNumber} onChange={e => setServiceNumber(e.target.value)} className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Broadband User ID</label>
                <div className={iw}>
                  <input value={bbUserId} onChange={e => setBbUserId(e.target.value)} className={ic} />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">OLT IP Address</label>
                <div className={iw}>
                  <input value={oltIp} onChange={e => setOltIp(e.target.value)} className={ic} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">VLAN ID</label>
                  <div className={iw}>
                    <input type="number" value={vlan} onChange={e => setVlan(e.target.value)} className={ic} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1">Port</label>
                  <div className={iw}>
                    <input type="number" value={port} onChange={e => setPort(e.target.value)} className={ic} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mismatch Management */}
          {task.mismatchFlag && (
            <div className="border-t border-red-200 pt-3 mt-3 bg-red-50/50 p-4 rounded-2xl border border-red-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-black text-red-700 uppercase tracking-widest flex items-center gap-1.5">
                  <AlertTriangle size={14} /> Mismatch Reported
                </span>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!mismatchFlag}
                    onChange={(e) => setMismatchFlag(!e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Resolve &amp; Clear Mismatch
                </label>
              </div>
              {mismatchFlag ? (
                <div>
                  <label className="block text-[10px] font-black text-red-400 uppercase mb-1">Engineer Comments</label>
                  <textarea
                    value={mismatchComments}
                    onChange={e => setMismatchComments(e.target.value)}
                    className="w-full text-xs font-semibold text-gray-750 placeholder-gray-400 border border-red-200 rounded-xl p-2 bg-white outline-none focus:border-red-400 transition-all resize-none"
                    rows={2}
                  />
                </div>
              ) : (
                <p className="text-[11px] text-green-600 font-bold">The mismatch flag will be cleared upon saving.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
          <button onClick={onClose} disabled={saving} className="flex-1 py-3 text-gray-600 bg-white border border-gray-200 font-bold rounded-2xl text-sm transition-colors hover:bg-gray-100 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-3 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
export const _TaskDetailModal = ({ task, onClose, isAdmin, onReload }: {
  task: Task; onClose: () => void; isAdmin: boolean
  onReload: () => void
}) => {
  const isEngineer = !isAdmin
  const t = TYPE_MAP[task.type]
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority]
  const displayType = task.type === 'others' && task.customType ? task.customType : t.label
  const statusLabel = getStatusLabel(task.status, isEngineer)
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  // Share state
  const [showShare, setShowShare] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)

  const taskLink = `${window.location.origin}/tasks?task=${task.taskNumber}`

  const shareMessage =
    `📋 BSNL FTTH Task: ${task.taskNumber}\n` +
    `Type: ${TYPE_MAP[task.type]?.label}\n` +
    `Customer: ${task.customerName}\n` +
    `Address: ${task.address}, ${task.city}\n` +
    `Status: ${statusLabel}\n` +
    `Priority: ${task.priority.toUpperCase()}\n` +
    (task.description ? `Details: ${task.description}\n` : '') +
    `\nView task: ${taskLink}`

  const handleSMS = () => {
    const phone = assignedPhone.replace(/\D/g, '')
    window.open(`sms:${phone}?body=${encodeURIComponent(shareMessage)}`, '_blank')
  }

  const handleWhatsApp = () => {
    const phone = assignedPhone.replace(/\D/g, '')
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(shareMessage)}`, '_blank')
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareMessage)
    setCopied(true)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  // Engineer assignment state
  const [engineers, setEngineers] = useState<FieldEngineer[]>([])
  const assignedPhone = task.assignedTo
    ? engineers.find(e => e.id === task.assignedTo || e.uid === task.assignedTo)?.phoneNumber ?? ''
    : ''
  const [engLoading, setEngLoading] = useState(false)
  const [showPicker, setShowPicker] = useState(false)
  const [engSearch, setEngSearch] = useState('')
  const [assigning, setAssigning] = useState(false)

  const canAssign = isAdmin && task.status !== 'closed' && task.status !== 'cancelled'

  useEffect(() => {
    if (!canAssign) return
    setEngLoading(true)
    engineerService.list().then(res => {
      if (res.success && res.data)
        setEngineers(res.data.filter(e => e.isActive && e.role === 'engineer'))
      setEngLoading(false)
    })
  }, [canAssign])

  const handleAssign = async (eng: FieldEngineer) => {
    setAssigning(true)
    const res = await taskService.assignEngineer(task.id, eng.id, eng.name)
    if (res.success) {
      toast.success(`Assigned to ${eng.name}`)
      onReload()
      onClose()
    } else {
      toast.error(res.error ?? 'Assignment failed')
    }
    setAssigning(false)
  }

  const filteredEngs = engineers.filter(e =>
    !engSearch ||
    e.name.toLowerCase().includes(engSearch.toLowerCase()) ||
    e.location.toLowerCase().includes(engSearch.toLowerCase())
  )

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-[2rem] sm:rounded-[2rem] overflow-hidden max-h-[95vh] sm:max-h-[90vh] flex flex-col"
        style={{ boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}
        onClick={e => e.stopPropagation()}>

        {/* ── Immersive dark header ── */}
        <div className="relative shrink-0 overflow-hidden"
          style={{ background: 'linear-gradient(160deg,#030a18 0%,#071428 50%,#0a1e3a 100%)' }}>

          {/* Large glow behind avatar */}
          <div className="absolute -top-12 -left-12 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle,${t.dot}40 0%,transparent 65%)`, filter: 'blur(32px)' }} />
          <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle,${t.dot}25 0%,transparent 65%)`, filter: 'blur(24px)' }} />

          {/* Priority bar at very top */}
          <div className="h-[3px] w-full" style={{ background: p.bar }} />

          {/* Mobile drag handle */}
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-white/20 rounded-full" />
          </div>

          {/* Main header row */}
          <div className="relative z-10 px-5 pt-3 sm:pt-5 pb-4 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3.5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-lg shrink-0"
                style={{ background: t.grad, boxShadow: `0 6px 20px ${t.dot}60` }}>
                {initials}
              </div>
              <div>
                <p className="font-black text-white text-[17px] leading-tight">{task.customerName}</p>
                <p className="text-[11px] font-mono text-blue-300/70 mt-0.5 tracking-wider">{task.taskNumber}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
                    {s.icon} {statusLabel}
                  </span>
                  <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${p.badge} ${p.badgeText}`}>
                    {p.label}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button onClick={() => setShowEditModal(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-2xl transition-all active:scale-90 bg-white/10 hover:bg-white/20 border border-white/12 text-white shrink-0"
                  title="Edit Task Details"
                >
                  <Pencil size={14} />
                </button>
              )}
              <button onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-2xl transition-all active:scale-90 shrink-0"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <X size={16} className="text-white" />
              </button>
            </div>
          </div>

          {/* Type pill + created date row */}
          <div className="relative z-10 px-5 pb-4 flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full text-white"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)' }}>
              {t.icon} {displayType}
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-white/50 ml-auto">
              <Calendar size={10} />
              {task.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 px-4 py-4 space-y-3" style={{ background: '#f8fafc' }}>

          {/* Contact + location quick-chips */}
          <div className="grid grid-cols-2 gap-2.5">
            {[
              { icon: Phone,  label: 'Phone',   value: `+91 ${task.customerPhone}`, color: '#2563eb' },
              { icon: MapPin, label: 'City',    value: task.city,                   color: '#059669' },
              ...(task.customerEmail ? [{ icon: Mail,    label: 'Email',   value: task.customerEmail,  color: '#7c3aed' }] : []),
            ].map(row => (
              <div key={row.label} className="bg-white rounded-2xl px-3.5 py-3 shadow-sm border border-gray-100">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="w-5 h-5 rounded-lg flex items-center justify-center" style={{ background: `${row.color}18` }}>
                    <row.icon size={10} style={{ color: row.color }} />
                  </div>
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{row.label}</p>
                </div>
                <p className="text-[13px] font-bold text-gray-800 truncate">{row.value}</p>
              </div>
            ))}
          </div>

          {/* Mismatch banner for admin */}
          {task.mismatchFlag && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-left">
              <div className="flex items-center gap-2 text-red-700 font-extrabold text-sm mb-1.5">
                <AlertTriangle size={16} className="animate-bounce" /> Details Mismatch Flagged
              </div>
              <p className="text-xs text-red-600 leading-relaxed font-semibold">"{task.mismatchComments}"</p>
              <p className="text-[10px] text-red-400 mt-2">Please correct the details below and clear this mismatch.</p>
            </div>
          )}

          {/* Address */}
          <div className="bg-white rounded-2xl px-4 py-3.5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-lg flex items-center justify-center" style={{ background: '#05966918' }}>
                <MapPin size={10} className="text-emerald-600" />
              </div>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Address</p>
            </div>
            <p className="text-sm font-semibold text-gray-800 leading-relaxed">{task.address}</p>
            {task.location && <p className="text-xs text-gray-500 mt-1">{task.location}</p>}
          </div>

          {/* BSNL Connection Details Card */}
          {(task.bbUserId || task.serviceNumber || task.oltIp || task.subscriptionPlan) && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3.5 text-left">
              <div className="flex items-center gap-2 border-b border-gray-150 pb-2">
                <div className="w-5 h-5 rounded-lg flex items-center justify-center bg-blue-50">
                  <Activity size={10} className="text-blue-500" />
                </div>
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">BSNL Connection Parameters</h3>
              </div>
              <div className="grid grid-cols-2 gap-3.5">
                {task.bbUserId && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Broadband User ID</span>
                    <span className="text-xs font-bold text-gray-800 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-gray-100 inline-block mt-0.5 select-all">{task.bbUserId}</span>
                  </div>
                )}
                {task.serviceNumber && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Service Number</span>
                    <span className="text-xs font-bold text-gray-800 mt-0.5 block">{task.serviceNumber}</span>
                  </div>
                )}
                {task.oltIp && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">OLT IP Address</span>
                    <span className="text-xs font-bold text-gray-800 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-gray-100 inline-block mt-0.5 select-all">{task.oltIp}</span>
                  </div>
                )}
                {(task.vlan !== null || task.port !== null) && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">VLAN / Port</span>
                    <span className="text-xs font-bold text-indigo-700 font-mono mt-0.5 block">
                      {task.vlan !== null ? task.vlan : '—'} / {task.port !== null ? task.port : '—'}
                    </span>
                  </div>
                )}
                {task.exchangeCode && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Exchange Code</span>
                    <span className="text-xs font-semibold text-gray-700 mt-0.5 block">{task.exchangeCode}</span>
                  </div>
                )}
                {task.category && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Category Code</span>
                    <span className="text-xs font-semibold text-gray-700 mt-0.5 block">{task.category}</span>
                  </div>
                )}
                {task.voipPassword && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">VoIP Password</span>
                    <span className="text-xs font-semibold text-gray-700 font-mono mt-0.5 block">{task.voipPassword}</span>
                  </div>
                )}
                {task.activationDate && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Activation Date</span>
                    <span className="text-xs font-semibold text-gray-750 font-mono mt-0.5 block">{task.activationDate}</span>
                  </div>
                )}
                {task.subscriptionPlan && (
                  <div className="col-span-2">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Subscription Plan</span>
                    <span className="text-xs font-bold text-gray-900 mt-0.5 block leading-normal">{task.subscriptionPlan}</span>
                  </div>
                )}
                {task.billingAccountNo && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Billing Account No.</span>
                    <span className="text-xs font-semibold text-gray-700 font-mono mt-0.5 block">{task.billingAccountNo}</span>
                  </div>
                )}
                {task.fmc !== undefined && (
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Monthly FMC</span>
                    <span className="text-xs font-extrabold text-emerald-600 mt-0.5 block">Rs. {task.fmc}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Speed Test Results Card (Admin View) */}
          {task.speedTestDownload && (
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3 text-left">
              <div className="flex items-center gap-2 border-b border-gray-150 pb-2">
                <div className="w-5 h-5 rounded-lg flex items-center justify-center bg-indigo-50">
                  <Gauge size={10} className="text-indigo-500" />
                </div>
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Modem Speed Test Verification</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-2.5">
                <div className="bg-slate-50/50 rounded-xl p-2 border border-slate-100 text-center">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Download</span>
                  <span className="text-sm font-extrabold text-blue-600 mt-0.5 block">{task.speedTestDownload} Mbps</span>
                </div>
                <div className="bg-slate-50/50 rounded-xl p-2 border border-slate-100 text-center">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Upload</span>
                  <span className="text-sm font-extrabold text-emerald-600 mt-0.5 block">{task.speedTestUpload} Mbps</span>
                </div>
                <div className="bg-slate-50/50 rounded-xl p-2 border border-slate-100 text-center">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block">Ping</span>
                  <span className="text-sm font-extrabold text-indigo-600 mt-0.5 block">{task.speedTestPing} ms</span>
                </div>
              </div>

              <div className="text-[10px] space-y-1 px-1 text-gray-500 pt-1.5">
                <div className="flex justify-between">
                  <span>Provider ISP:</span>
                  <span className="font-bold text-gray-700 truncate max-w-[190px]">{task.speedTestIsp}</span>
                </div>
                <div className="flex justify-between">
                  <span>Client IP:</span>
                  <span className="font-mono text-gray-650">{task.speedTestIp}</span>
                </div>
                {task.speedTestTimestamp && (
                  <div className="flex justify-between">
                    <span>Tested At:</span>
                    <span className="text-gray-600">
                      {new Date(task.speedTestTimestamp).toLocaleString('en-IN', { hour12: true, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Assigned engineer chip */}
          {task.assignedEngineerName && (
            <div className="bg-white rounded-2xl px-4 py-3.5 flex items-center gap-3 shadow-sm"
              style={{ border: '1px solid rgba(37,99,235,0.15)' }}>
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white text-xs font-black shrink-0"
                style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)', boxShadow: '0 4px 10px rgba(37,99,235,0.25)' }}>
                {task.assignedEngineerName.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Assigned Engineer</p>
                <p className="text-sm font-bold text-gray-800">{task.assignedEngineerName}</p>
              </div>
              <UserCheck size={18} className="text-blue-400 shrink-0" />
            </div>
          )}

          {/* Assign / Reassign section for admin */}
          {canAssign && (
            <div className="space-y-2">
              <button
                onClick={() => setShowPicker(v => !v)}
                className="w-full flex items-center gap-3 px-4 py-3.5 bg-white rounded-2xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 transition-all group shadow-sm active:scale-[0.98]">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors"
                  style={{ background: showPicker ? 'linear-gradient(135deg,#2563eb,#06b6d4)' : '#eff6ff' }}>
                  <UserCheck size={15} className={showPicker ? 'text-white' : 'text-blue-500'} />
                </div>
                <p className="flex-1 text-sm font-bold text-gray-700 group-hover:text-blue-600 text-left">
                  {task.assignedEngineerName ? 'Reassign engineer' : 'Assign to an engineer'}
                </p>
                <ChevronDown size={15} className={`text-gray-400 transition-transform duration-200 ${showPicker ? 'rotate-180' : ''}`} />
              </button>

              {showPicker && (
                <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-md bg-white">
                  <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 bg-gray-50">
                    <User size={13} className="text-gray-400 shrink-0" />
                    <input value={engSearch} onChange={e => setEngSearch(e.target.value)}
                      placeholder="Search by name or location…"
                      className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent"
                      autoFocus />
                    {engSearch && <button onClick={() => setEngSearch('')}><X size={13} className="text-gray-400" /></button>}
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {engLoading ? (
                      <div className="flex items-center justify-center py-6 gap-2 text-gray-400">
                        <Loader2 size={14} className="animate-spin" />
                        <span className="text-xs">Loading…</span>
                      </div>
                    ) : filteredEngs.length === 0 ? (
                      <p className="py-6 text-center text-xs text-gray-400">
                        {engineers.length === 0 ? 'No active engineers found.' : 'No match.'}
                      </p>
                    ) : filteredEngs.map((eng, i) => (
                      <button key={eng.id}
                        disabled={assigning}
                        onClick={() => handleAssign(eng)}
                        className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors text-left group disabled:opacity-60 ${i > 0 ? 'border-t border-gray-50' : ''}`}>
                        <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white text-xs font-black shrink-0"
                          style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)' }}>
                          {eng.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold text-gray-800 group-hover:text-blue-700 transition-colors">
                            {eng.name}
                            {(task.assignedTo === eng.id || task.assignedTo === eng.uid) && <span className="ml-2 text-[10px] text-blue-400">(current)</span>}
                          </p>
                          <p className="text-[11px] text-gray-400">{eng.location}</p>
                        </div>
                        {assigning
                          ? <Loader2 size={13} className="animate-spin text-blue-400 shrink-0" />
                          : <ArrowRight size={13} className="text-gray-300 group-hover:text-blue-400 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Share Task ── */}
          {isAdmin && task.assignedTo && (
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              <button
                onClick={() => setShowShare(v => !v)}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-emerald-50/40 transition-colors group active:scale-[0.98]">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: showShare ? 'linear-gradient(135deg,#10b981,#059669)' : '#ecfdf5' }}>
                  <Share2 size={15} className={showShare ? 'text-white' : 'text-emerald-600'} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-black text-gray-800">Share with Engineer</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {task.assignedEngineerName ? `Send to ${task.assignedEngineerName}` : 'Send task details & link'}
                  </p>
                </div>
                <ChevronDown size={15} className={`text-gray-400 transition-transform duration-200 ${showShare ? 'rotate-180' : ''}`} />
              </button>

              {showShare && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
                  <div className="mt-2 bg-gray-50 rounded-2xl px-3.5 py-3 max-h-28 overflow-y-auto">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Message Preview</p>
                    <p className="text-[11px] text-gray-600 whitespace-pre-line leading-relaxed">{shareMessage}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={handleWhatsApp}
                      className="flex flex-col items-center gap-2 py-3.5 rounded-2xl font-black text-[11px] text-white transition-all active:scale-95"
                      style={{ background: 'linear-gradient(135deg,#25d366,#128c7e)', boxShadow: '0 4px 12px rgba(37,211,102,0.3)' }}>
                      <MessageSquare size={19} /> WhatsApp
                    </button>
                    <button onClick={handleSMS}
                      className="flex flex-col items-center gap-2 py-3.5 rounded-2xl font-black text-[11px] text-white transition-all active:scale-95"
                      style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', boxShadow: '0 4px 12px rgba(59,130,246,0.3)' }}>
                      <Phone size={19} /> SMS
                    </button>
                    <button onClick={handleCopy}
                      className={`flex flex-col items-center gap-2 py-3.5 rounded-2xl font-black text-[11px] transition-all active:scale-95 ${
                        copied ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {copied ? <CheckCheck size={19} /> : <Copy size={19} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  {assignedPhone && (
                    <p className="text-[10px] text-center text-gray-400">
                      To: <span className="font-black text-gray-600">{assignedPhone}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Site photos */}
          {task.sitePhotos && task.sitePhotos.length > 0 && (
            <div className="rounded-2xl px-3.5 py-3" style={{ background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.1)' }}>
              <div className="flex items-center gap-1.5 mb-2">
                <MapPin size={11} className="text-indigo-400" />
                <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Site Photos · {task.sitePhotos.length}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {task.sitePhotos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="w-16 h-16 rounded-xl overflow-hidden border border-indigo-100 shrink-0 hover:opacity-80 transition-opacity">
                    <img src={url} alt={`site-${i}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Feasibility Study Summary (for enquiries) */}
          {task.type === 'enquiry' && task.feasibilityStatus && (
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100"
              style={{ borderLeft: task.feasibilityStatus === 'feasible' ? '4px solid #10b981' : '4px solid #ef4444' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-slate-50/50">
                <div className="flex items-center gap-2">
                  <Zap size={13} className="text-indigo-500 animate-pulse" />
                  <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Feasibility Study</p>
                </div>
                <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${
                  task.feasibilityStatus === 'feasible' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                }`}>
                  {task.feasibilityStatus === 'feasible' ? '⚡ FEASIBLE' : '❌ NOT FEASIBLE'}
                </span>
              </div>
              <div className="p-4 space-y-3">
                {task.feasibilityComments && (
                  <div>
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Additional Comments</p>
                    <p className="text-xs text-gray-700 leading-relaxed mt-1 font-semibold">{task.feasibilityComments}</p>
                  </div>
                )}
                {task.feasibilityWorkflowRequired && task.feasibilityWorkflowRequired.length > 0 && (
                  <div>
                    <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Required Additional Workflows</p>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {task.feasibilityWorkflowRequired.map(wf => (
                        <span key={wf} className="text-[10px] bg-slate-100 text-slate-700 border border-gray-200/50 px-2.5 py-0.5 rounded-lg font-bold">
                          {wf}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {task.feasibilityLatitude !== undefined && task.feasibilityLongitude !== undefined && (
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">GPS Coordinates</p>
                      <p className="text-xs font-mono text-gray-700 font-extrabold mt-0.5">
                        {task.feasibilityLatitude.toFixed(6)}, {task.feasibilityLongitude.toFixed(6)}
                      </p>
                    </div>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${task.feasibilityLatitude},${task.feasibilityLongitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-black text-indigo-655 hover:text-indigo-855 bg-indigo-50 border border-indigo-100/60 px-2.5 py-1.5 rounded-xl transition-all flex items-center gap-1.5 shadow-sm"
                    >
                      <MapPin size={11} className="text-indigo-500" /> Open on Map
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {task.description && (
            <div className="bg-white rounded-2xl px-4 py-3.5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-lg flex items-center justify-center" style={{ background: '#2563eb18' }}>
                  <FileText size={10} className="text-blue-600" />
                </div>
                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Description</p>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{task.description}</p>
            </div>
          )}

          {task.notes && (
            <div className="bg-white rounded-2xl px-4 py-3.5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-lg flex items-center justify-center" style={{ background: '#f59e0b18' }}>
                  <StickyNote size={10} className="text-amber-500" />
                </div>
                <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest">Internal Notes</p>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{task.notes}</p>
            </div>
          )}
        </div>

        {/* Customer feedback summary (closed tasks) */}
        {task.status === 'closed' && task.customerRating && (
          <div className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.12)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b"
              style={{ borderColor: 'rgba(168,85,247,0.1)' }}>
              <div className="flex items-center gap-2">
                <Star size={12} className="text-purple-400" />
                <p className="text-[9px] font-black text-purple-500 uppercase tracking-widest">Customer Feedback</p>
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className={`text-sm leading-none ${i < task.customerRating! ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                ))}
                <span className="text-xs font-black text-gray-700 ml-1">
                  {['','Poor','Fair','Good','Very Good','Excellent'][task.customerRating]}
                </span>
              </div>
            </div>
            {/* Category breakdown */}
            {task.feedbackCategories && (
              <div className="grid grid-cols-2 gap-0 divide-x divide-y"
                style={{ borderColor: 'rgba(168,85,247,0.08)' }}>
                {([
                  { key: 'serviceQuality', label: 'Service Quality' },
                  { key: 'responseTime',   label: 'Response Time'   },
                  { key: 'communication',  label: 'Communication'   },
                  { key: 'workmanship',    label: 'Workmanship'     },
                ] as const).map(({ key, label }) => (
                  <div key={key} className="px-3 py-2">
                    <p className="text-[9px] text-gray-400 font-bold">{label}</p>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className={`text-xs leading-none ${i < (task.feedbackCategories?.[key] ?? 0) ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Recommend + comment */}
            <div className="px-3.5 py-2.5 flex items-start gap-3 border-t"
              style={{ borderColor: 'rgba(168,85,247,0.1)' }}>
              {task.wouldRecommend !== undefined && (
                <span className={`shrink-0 text-[10px] font-black px-2.5 py-1 rounded-full ${task.wouldRecommend ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                  {task.wouldRecommend ? '👍 Would Recommend' : '👎 Would Not Recommend'}
                </span>
              )}
              {task.customerFeedback && (
                <p className="text-xs text-gray-600 italic leading-relaxed">"{task.customerFeedback}"</p>
              )}
            </div>
          </div>
        )}

        {/* View Details footer */}
        <div className="shrink-0 px-5 pb-5 pt-3 border-t border-gray-50">
          <button
            onClick={() => { window.location.href = `/tasks/${task.id}`; onClose() }}
            className="w-full py-3.5 text-white font-black rounded-2xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
            <ArrowRight size={16} /> Open Task Details
          </button>
        </div>
      </div>
      {showEditModal && (
        <EditTaskModal
          task={task}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false)
            onReload()
            onClose()
          }}
        />
      )}
    </div>,
    document.body
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const TaskListPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const engineerId = user?.uid ?? user?.id ?? ''

  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [seeding, setSeeding] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>(
    location.state?.status || 'all'
  )
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>('all')
  const [viewMode, setViewMode] = useState<'tile' | 'list'>('tile')
  type DateFilterValue = 'week' | 'month' | 'three_months' | 'all'
  const [dateFilter, setDateFilter] = useState<DateFilterValue>('month')

  useEffect(() => {
    if (location.state?.status) {
      setStatusFilter(location.state.status)
    }
  }, [location.state?.status])

  const load = useCallback(async () => {
    setLoading(true)
    if (isAdmin) {
      const res = await taskService.list()
      if (res.success && res.data) setTasks(res.data)
      else toast.error(res.error ?? 'Failed to load tasks')
    } else {
      // Engineers only see tasks assigned to them + open/claimable tasks
      const myIds = [engineerId, user?.engineerDocId].filter(Boolean) as string[]
      const [mine, open] = await Promise.all([
        taskService.listByAssignee(engineerId, user?.engineerDocId),
        taskService.listUnassigned(),
      ])
      // Strict filter: only tasks where assignedTo matches this engineer
      const myTasks = (mine.data ?? []).filter(t => t.assignedTo && myIds.includes(t.assignedTo))
      // Open tasks must truly be status=open (unassigned)
      const openTasks = (open.data ?? []).filter(t => t.status === 'open' && !t.assignedTo)
      const combined = [...myTasks, ...openTasks]
      const unique = combined.filter((t, i) => combined.findIndex(x => x.id === t.id) === i)
      unique.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      setTasks(unique)
    }
    setLoading(false)
  }, [isAdmin, engineerId, user?.engineerDocId])

  useEffect(() => { load() }, [load])

  const handleClearAll = async () => {
    if (!window.confirm('Delete ALL docket tickets? This cannot be undone.')) return
    setClearing(true)
    const res = await taskService.deleteAllTasks()
    if (res.success) { toast.success(`${res.data} ticket(s) deleted`); setTasks([]) }
    else toast.error(res.error ?? 'Delete failed')
    setClearing(false)
  }

  const handleSeed = async () => {
    setSeeding(true)
    const res = await taskService.seedSampleTasks(user?.uid ?? user?.id ?? 'system')
    if (res.success) { toast.success(`${res.data} sample tasks created!`); load() }
    else toast.error(res.error ?? 'Seed failed')
    setSeeding(false)
  }

  const getTaskPhase = (t: Task): PhaseFilter => {
    if (t.installationPhase === 'completed' || t.status === 'closed') return 'completed'
    if (t.installationPhase && t.installationPhase !== 'pending') return 'installation'
    if (t.provisioningStage === 'provisioned') return 'installation'
    if (t.feasibilityApproved || (t.provisioningStage && t.provisioningStage !== 'pending')) return 'provisioning'
    if (t.feasibilityStatus) return 'feasibility'
    return 'enquiry'
  }

  const filtered = tasks.filter(t => {
    const matchType = t.type === 'enquiry'

    const matchStatus = statusFilter === 'all'
      ? true
      : statusFilter === 'in_progress'
        ? ['assigned', 'in_progress', 'site_visit', 'work_in_progress', 'pending_feedback'].includes(t.status)
        : t.status === statusFilter

    const matchPhase = phaseFilter === 'all' ? true : getTaskPhase(t) === phaseFilter

    const now = new Date()
    const taskTime = t.createdAt.getTime()
    let matchDate = true
    if (dateFilter === 'week') {
      matchDate = now.getTime() - taskTime <= 7 * 24 * 60 * 60 * 1000
    } else if (dateFilter === 'month') {
      matchDate = now.getTime() - taskTime <= 30 * 24 * 60 * 60 * 1000
    } else if (dateFilter === 'three_months') {
      matchDate = now.getTime() - taskTime <= 90 * 24 * 60 * 60 * 1000
    }

    const q = search.toLowerCase()
    const matchSearch = !q ||
      t.customerName.toLowerCase().includes(q) ||
      t.customerPhone.includes(q) ||
      t.taskNumber.toLowerCase().includes(q) ||
      t.city.toLowerCase().includes(q)

    return matchType && matchStatus && matchPhase && matchDate && matchSearch
  })

  const getTabCount = (filterVal: string) => {
    const now = new Date()
    const filteredByDate = tasks.filter(t => {
      const taskTime = t.createdAt.getTime()
      if (dateFilter === 'week') {
        return now.getTime() - taskTime <= 7 * 24 * 60 * 60 * 1000
      } else if (dateFilter === 'month') {
        return now.getTime() - taskTime <= 30 * 24 * 60 * 60 * 1000
      } else if (dateFilter === 'three_months') {
        return now.getTime() - taskTime <= 90 * 24 * 60 * 60 * 1000
      }
      return true
    })

    const enquiryOnly = filteredByDate.filter(t => t.type === 'enquiry')
    const phaseMatched = phaseFilter === 'all'
      ? enquiryOnly
      : enquiryOnly.filter(t => getTaskPhase(t) === phaseFilter)

    if (filterVal === 'all') return phaseMatched.length
    if (filterVal === 'in_progress') {
      return phaseMatched.filter(t => ['assigned', 'in_progress', 'site_visit', 'work_in_progress', 'pending_feedback'].includes(t.status)).length
    }
    const statusCounts = phaseMatched.reduce((acc, t) => {
      acc[t.status] = (acc[t.status] ?? 0) + 1; return acc
    }, {} as Record<string, number>)
    return statusCounts[filterVal] ?? 0
  }

  const getPhaseCount = (filterVal: PhaseFilter) => {
    const now = new Date()
    const filteredByDate = tasks.filter(t => {
      const taskTime = t.createdAt.getTime()
      if (dateFilter === 'week') {
        return now.getTime() - taskTime <= 7 * 24 * 60 * 60 * 1000
      } else if (dateFilter === 'month') {
        return now.getTime() - taskTime <= 30 * 24 * 60 * 60 * 1000
      } else if (dateFilter === 'three_months') {
        return now.getTime() - taskTime <= 90 * 24 * 60 * 60 * 1000
      }
      return true
    })

    const enquiryOnly = filteredByDate.filter(t => t.type === 'enquiry')
    if (filterVal === 'all') return enquiryOnly.length
    return enquiryOnly.filter(t => getTaskPhase(t) === filterVal).length
  }

  return (
    <div className="space-y-5">

      {/* ── Hero + filter strip ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 55%,#0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.3) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-0 w-48 h-32 rounded-full"
            style={{ background: 'radial-gradient(ellipse,rgba(37,99,235,0.2) 0%,transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-5 flex items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-2">
              <ClipboardList size={10} className="text-blue-300" />
              <span className="text-[10px] text-blue-200 font-semibold uppercase tracking-wider">Docket Requests</span>
            </div>
            <h1 className="text-[22px] font-black leading-tight">Docket Requests</h1>
            <p className="text-blue-300/70 text-sm mt-0.5">
              {getTabCount('all')} total · <span className="text-sky-300 font-bold">{getTabCount('open')} open</span>
              {getTabCount('in_progress') > 0 && <> · <span className="text-violet-300 font-bold">{getTabCount('in_progress')} active</span></>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center rounded-xl p-1 gap-0.5" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <button onClick={() => setViewMode('tile')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'tile' ? 'bg-white/20 text-white' : 'text-white/40'}`}><LayoutGrid size={14} /></button>
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white/20 text-white' : 'text-white/40'}`}><List size={14} /></button>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'enquiry' } })}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-white rounded-2xl text-[13px] font-black transition-all active:scale-95"
                  style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', boxShadow: '0 4px 14px rgba(37,99,235,0.4)' }}>
                  <Plus size={14} /> New
                </button>
                <button onClick={handleClearAll} disabled={clearing || tasks.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-2xl text-[13px] font-black transition-all active:scale-95 disabled:opacity-40"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.25)' }}>
                  {clearing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {clearing ? 'Clearing…' : 'Clear All'}
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Scrollable status filter chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-5 pb-2">
          {STATUS_FILTERS.map(f => {
            const cnt = getTabCount(f.value)
            const isActive = statusFilter === f.value
            return (
              <button key={f.value} onClick={() => setStatusFilter(f.value)}
                className="shrink-0 flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full text-[11px] font-black transition-all active:scale-95"
                style={isActive
                  ? { background: 'rgba(255,255,255,0.95)', color: '#1e40af', boxShadow: '0 2px 12px rgba(37,99,235,0.3)' }
                  : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.12)' }}>
                {f.icon}
                {f.label}
                {cnt > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black"
                    style={isActive ? { background: 'rgba(37,99,235,0.15)', color: '#1e40af' } : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)' }}>
                    {cnt}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {/* Phase filter chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-5 pb-4">
          {PHASE_FILTERS.map(f => {
            const cnt = getPhaseCount(f.value)
            const isActive = phaseFilter === f.value
            return (
              <button key={f.value} onClick={() => setPhaseFilter(f.value)}
                className="shrink-0 flex items-center gap-1.5 pl-2.5 pr-3 py-1 rounded-full text-[10px] font-black transition-all active:scale-95"
                style={isActive
                  ? { background: 'rgba(16,185,129,0.2)', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)' }
                  : { background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {f.icon}
                {f.label}
                {cnt > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black"
                    style={isActive ? { background: 'rgba(16,185,129,0.2)', color: '#059669' } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
                    {cnt}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Search & Date Filter ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2.5 rounded-2xl px-4 py-3 bg-white border border-gray-200/80 shadow-sm focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 transition-all">
          <Search size={14} className="text-gray-400 shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name, phone, task ID, city…"
            className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-400 min-w-0" />
          {search && (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={13} />
            </button>
          )}
          {filtered.length > 0 && search && (
            <span className="text-[10px] font-black text-blue-600 shrink-0 bg-blue-50 px-2 py-0.5 rounded-full">{filtered.length} found</span>
          )}
        </div>

        {/* Date Filter Dropdown */}
        <div className="sm:w-56 relative">
          <select
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value as DateFilterValue)}
            className="w-full bg-white border border-gray-200/80 rounded-2xl pl-3.5 pr-10 py-3 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none cursor-pointer transition-all shadow-sm"
          >
            <option value="week">Past Week</option>
            <option value="month">Past Month (30 days)</option>
            <option value="three_months">Past 3 Months</option>
            <option value="all">All Time</option>
          </select>
          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
            <ChevronDown size={14} />
          </div>
        </div>
      </div>

      {/* ── Results ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,rgba(37,99,235,0.1),rgba(6,182,212,0.1))' }}>
            <Loader2 size={22} className="text-blue-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading tasks…</p>
        </div>

      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,rgba(37,99,235,0.07),rgba(99,102,241,0.05))', border: '1px solid rgba(99,102,241,0.1)' }}>
            <AlertTriangle size={26} className="text-gray-300" />
          </div>
          <div>
            <p className="font-black text-gray-700 text-base">{search ? 'No results found' : 'No dockets yet'}</p>
            <p className="text-sm text-gray-400 mt-1">{search ? `Nothing matched "${search}"` : 'Create your first docket request'}</p>
          </div>
          {!search && isAdmin && (
            <div className="flex flex-col sm:flex-row gap-2.5">
              <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'enquiry' } })}
                className="px-5 py-2.5 text-sm font-bold text-white rounded-xl"
                style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                + New Docket
              </button>
              <button onClick={handleSeed} disabled={seeding}
                className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-all disabled:opacity-60">
                {seeding ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {seeding ? 'Creating…' : 'Seed Sample Tasks'}
              </button>
            </div>
          )}
        </div>

      ) : viewMode === 'tile' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(task => (
            <TileCard key={task.id} task={task} onClick={() => navigate(`/tasks/${task.id}`)} isEngineer={!isAdmin} />
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map(task => (
            <ListRow key={task.id} task={task} onClick={() => navigate(`/tasks/${task.id}`)} isEngineer={!isAdmin} />
          ))}
        </div>
      )}

    </div>
  )
}

export default TaskListPage
